import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  HardDrive, Folder, FileText, ChevronRight, ArrowLeft, RefreshCw,
  Link2, Settings, AlertTriangle, CheckCircle2, Upload, Database,
  FileImage, Film as FilmIcon, Music, File, Code, Download, Copy, Terminal
} from 'lucide-react';

const EXT_ICONS = {
  '.blend': Code, '.ma': Code, '.mb': Code, '.nk': Code,
  '.psd': FileImage, '.ai': FileImage, '.png': FileImage, '.jpg': FileImage, '.jpeg': FileImage, '.tiff': FileImage, '.exr': FileImage, '.dpx': FileImage,
  '.mov': FilmIcon, '.mp4': FilmIcon, '.avi': FilmIcon,
  '.wav': Music, '.mp3': Music, '.aac': Music,
  '.pdf': FileText, '.doc': FileText, '.txt': FileText, '.pur': FileText,
};

const EXT_COLORS = {
  '.blend': '#f59e0b', '.ma': '#06b6d4', '.mb': '#06b6d4', '.nk': '#10b981',
  '.psd': '#3b82f6', '.ai': '#f97316', '.png': '#8b5cf6', '.jpg': '#8b5cf6',
  '.mov': '#ec4899', '.mp4': '#ec4899', '.wav': '#14b8a6', '.mp3': '#14b8a6',
  '.pdf': '#ef4444', '.pur': '#d946ef',
};

export default function NASBrowserPage({ projectId, projectName, api }) {
  const [status, setStatus] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapping, setMapping] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showBridgeSetup, setShowBridgeSetup] = useState(false);
  const [bridgeScript, setBridgeScript] = useState(null);
  const [copied, setCopied] = useState(false);
  const [mapForm, setMapForm] = useState({ nas_folder: '' });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [bridgeAgents, setBridgeAgents] = useState([]);
  const [showCreateFolders, setShowCreateFolders] = useState(false);
  const [folderBase, setFolderBase] = useState('');
  const [folderName, setFolderName] = useState('');
  const [folderResult, setFolderResult] = useState(null);
  // Picker state (browse-NAS within Create modal)
  const [pickerPath, setPickerPath] = useState('');
  const [pickerDirs, setPickerDirs] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  // Job polling state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  useEffect(() => {
    api.get('/nas/status').then(r => {
      setStatus(r.data);
      // Only show as connected if at least one agent is fresh (last_seen < 5min)
      setBridgeAgents(r.data.fresh_agents && r.data.fresh_agents.length > 0 ? r.data.fresh_agents : []);
    }).catch(() => {});
    if (projectId) {
      api.get(`/nas/project-mapping/${projectId}`).then(r => setMapping(r.data)).catch(() => {});
    }
  }, [api, projectId]);

  const downloadScript = async () => {
    try {
      const res = await api.get('/nas/bridge/download-script');
      setBridgeScript(res.data);
      setShowBridgeSetup(true);
    } catch (e) { console.error(e); }
  };

  const copyScript = () => {
    if (bridgeScript?.content) {
      navigator.clipboard.writeText(bridgeScript.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const copyInstaller = () => {
    if (bridgeScript?.installer_content) {
      navigator.clipboard.writeText(bridgeScript.installer_content);
      setCopied('installer');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const browse = async (path = '') => {
    setLoading(true);
    setCurrentPath(path);
    try {
      let res;
      if (hasBridge) {
        // Send command through bridge agent
        res = await api.post('/nas/bridge/send-command', { action: 'list_dir', path: path });
        if (res.data && res.data.status === 'bridge_stale') {
          setFiles([]);
          alert(res.data.message || 'NAS bridge has not polled in 5+ minutes. Start vos_bridge.sh on your QNAP.');
        } else if (res.data && res.data.files) {
          setFiles(res.data.files);
        } else if (res.data && res.data.status === 'timeout') {
          setFiles([]);
          alert('Bridge did not respond in 10s. Queue may be clogged - try "Clear Stale Queue" from the gear menu.');
        } else {
          setFiles(res.data?.files || []);
        }
        setLoading(false);
        return;
      } else if (projectId && mapping && !mapping.status) {
        res = await api.get(`/nas/project-files/${projectId}?subfolder=${encodeURIComponent(path)}`);
      } else {
        res = await api.get(`/nas/browse?path=${encodeURIComponent(path)}`);
      }
      setFiles(res.data.files || []);
    } catch (e) { setFiles([]); }
    setLoading(false);
  };

  const clearStaleQueue = async () => {
    try {
      const res = await api.post('/nas/bridge/clear-stale-queue');
      alert(`Cleared ${res.data.cleared} stale commands from the queue.`);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const navigateUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    browse(parts.join('/'));
  };

  const mapProject = async () => {
    await api.post('/nas/map-to-project', { project_id: projectId, nas_folder: mapForm.nas_folder });
    const m = await api.get(`/nas/project-mapping/${projectId}`);
    setMapping(m.data);
    setShowMap(false);
    browse('');
  };

  const syncFromNAS = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/nas/sync-library-from-nas/${projectId}`);
      setSyncResult(res.data);
    } catch (e) { console.error(e); }
    setSyncing(false);
  };

  const linkToLibrary = async (filePath) => {
    // This would open a picker to link to a library item
    // For now just show the path
    alert(`NAS path to link: ${filePath}\n\nUse Master Library to create an item and link this file.`);
  };

  // --- Folder-structure picker helpers ---
  // Browsing INTO a folder auto-selects it as the target — matches user
  // mental model ("I'm in 01FAIRY_TALES, so this IS my target"). Prevents
  // the silent bug where the form sent nas_base="" while the user thought
  // they were creating inside the folder they navigated to.
  const browsePicker = async (path = '') => {
    setPickerLoading(true);
    setPickerPath(path);
    setFolderBase(path);
    try {
      const res = await api.post('/nas/bridge/send-command', { action: 'list_dir', path });
      const all = res.data?.files || [];
      setPickerDirs(all.filter(f => f.is_dir));
    } catch (e) {
      setPickerDirs([]);
    }
    setPickerLoading(false);
  };

  const pickerUp = () => {
    const parts = pickerPath.split('/').filter(Boolean);
    parts.pop();
    browsePicker(parts.join('/'));
  };

  const submitCreateFolders = async () => {
    try {
      setFolderResult(null);
      const res = await api.post(`/projects/${projectId}/create-nas-folders`,
        { nas_base: folderBase, project_folder: folderName });
      setFolderResult(res.data);
      if (res.data?.job_id) {
        setJobId(res.data.job_id);
        pollJob(res.data.job_id);
      }
    } catch (e) {
      setFolderResult({ error: e.response?.data?.detail || 'Failed to queue folder creation' });
    }
  };

  const pollJob = async (id) => {
    for (let i = 0; i < 60; i++) {
      try {
        const res = await api.get(`/projects/${projectId}/nas-folder-job/${id}`);
        setJobStatus(res.data);
        if (res.data?.done) break;
      } catch (e) { /* ignore */ }
      await new Promise(r => setTimeout(r, 2000));
    }
  };

  const isConfigured = status?.configured || (status?.bridge_agents || []).length > 0;
  const isConnected = status?.bridge_connected;
  const isMapped = mapping && !mapping.status;
  const hasBridge = bridgeAgents.length > 0;
  const queueInfo = status?.queue || {};
  const hasStaleQueue = (queueInfo.interactive_pending || 0) + (queueInfo.batch_pending || 0) > 20;
  const lastSeenAgo = (bridgeAgents?.[0]?.age_seconds ?? status?.bridge_agents?.[0]?.age_seconds);
  const liveAgentLabel = bridgeAgents?.[0]?.agent_id || bridgeAgents?.[0]?.nas_host || status?.bridge_agents?.[0]?.nas_host || '172.16.16.3';
  const lastSeenLabel = lastSeenAgo == null ? 'never'
    : lastSeenAgo < 60 ? `${lastSeenAgo}s ago`
    : lastSeenAgo < 3600 ? `${Math.floor(lastSeenAgo / 60)}m ago`
    : `${Math.floor(lastSeenAgo / 3600)}h ago`;

  return (
    <div className="space-y-4" data-testid="nas-browser">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-blue-400" />
          <div>
            <h2 className="text-lg font-heading font-bold text-white">NAS Storage</h2>
            <p className="text-[10px] text-zinc-400">
              {hasBridge ? `Bridge Agent LIVE (${liveAgentLabel}) · last ping ${lastSeenLabel}`
                : (status?.bridge_agents?.length || 0) > 0 ? `Bridge STALE — last seen ${lastSeenLabel}. Start vos_bridge.sh on QNAP.`
                : 'Not configured - run Bridge Agent on studio PC'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {hasStaleQueue && (
            <button data-testid="clear-queue-btn" onClick={clearStaleQueue}
              className="h-7 px-3 bg-red-600 hover:bg-red-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5"
              title={`${(queueInfo.interactive_pending || 0) + (queueInfo.batch_pending || 0)} pending commands in queue`}>
              <AlertTriangle className="w-3 h-3" /> Clear Stale Queue ({(queueInfo.interactive_pending || 0) + (queueInfo.batch_pending || 0)})
            </button>
          )}
          {isMapped && (
            <button data-testid="sync-nas-btn" onClick={syncFromNAS} disabled={syncing}
              className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync to Library'}
            </button>
          )}
          <button onClick={() => {
              // Pre-fill the project name into the input so the user doesn't
              // mistake the placeholder hint for typed content (the silent
              // `disabled={!folderName}` trap that left the Create button
              // greyed out even after picking a parent folder).
              setFolderName((projectName || '').replace(/\s+/g, '_'));
              setShowCreateFolders(true);
            }}
            className="h-7 px-3 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <Database className="w-3 h-3" /> Create Folder Structure
          </button>
          {projectId && !isMapped && isConnected && (
            <button data-testid="map-nas-btn" onClick={() => setShowMap(true)}
              className="h-7 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
              <Link2 className="w-3 h-3" /> Map NAS Folder
            </button>
          )}
          <button onClick={() => setShowConfig(!showConfig)} className="h-7 px-2 bg-zinc-800 text-zinc-400 text-[10px] rounded-sm hover:bg-zinc-700">
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className="flex gap-3">
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-medium ${isConfigured ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {isConfigured ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {isConfigured ? 'Configured' : 'Not Configured'}
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-medium ${isConnected ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {isConnected ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {isConnected ? `Bridge LIVE · ${lastSeenLabel}` : `Bridge STALE · last seen ${lastSeenLabel}`}
        </div>
        {isMapped && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <Database className="w-3 h-3" /> Mapped: {mapping.nas_folder}
          </div>
        )}
      </div>

      {/* Re-root warning — daemon is sandboxed to a sub-path under Projects.
          Show the one-line SSH fix so the user can browse the full share. */}
      {(() => {
        const basePath = String(bridgeAgents[0]?.nas_base_path || '');
        // Sandboxed when the daemon's root contains a sub-folder under
        // /Projects/ (e.g. /share/CACHEDEV1_DATA/Projects/AS_Project) or
        // when it's a relative sub-path (e.g. "AS_Project"). Share root
        // itself (/share/CACHEDEV1_DATA/Projects, "Projects" or empty)
        // means the bridge is at the top — no banner.
        const m = basePath.match(/[/\\]Projects[/\\]([^/\\].*)/i);
        const looksRelativeSub = basePath && !basePath.includes('/') && !basePath.includes('\\') && basePath.toLowerCase() !== 'projects';
        const sandboxed = !!(m && m[1]) || looksRelativeSub;
        const sandboxLabel = (m && m[1]) || (looksRelativeSub ? basePath : '');
        if (!hasBridge || !sandboxed) return null;
        const sshCmd = `sed -i 's|VOS_NAS_PATH="/share/CACHEDEV1_DATA/Projects/${sandboxLabel}"|VOS_NAS_PATH="/share/CACHEDEV1_DATA/Projects"|g' /share/CACHEDEV1_DATA/Projects/vos_bridge/start.sh && for pid in $(ps | grep vos_bridge_daemon | grep -v grep | awk '{print \$1}'); do kill -9 $pid 2>/dev/null; done; sleep 2; rm -f /share/CACHEDEV1_DATA/Projects/vos_bridge/vos_bridge.pid; sh /share/CACHEDEV1_DATA/Projects/vos_bridge/start.sh && echo "OK"`;
        return (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-4" data-testid="reroot-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-300">
                  Bridge is sandboxed to <span className="font-mono text-amber-200">{sandboxLabel}/</span> — re-root to the share root to see all projects (Fairy_Tales, GOGO_The_Robot, etc.)
                </p>
                <p className="text-[10px] text-amber-400/70 mt-1">
                  One-time fix: SSH into the QNAP and paste the command below. Takes ~5 seconds; the bridge auto-reconnects.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <pre className="flex-1 text-[10px] font-mono text-amber-100 bg-zinc-950 border border-amber-500/30 rounded-sm px-3 py-2 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all" data-testid="reroot-ssh-command">{sshCmd}</pre>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(sshCmd);
                      setCopied('reroot');
                      setTimeout(() => setCopied(false), 3000);
                    }}
                    className="h-9 px-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[11px] font-bold rounded-sm flex items-center gap-1.5 flex-shrink-0"
                    data-testid="reroot-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" /> {copied === 'reroot' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-[10px] text-amber-400/60 mt-2 italic">
                  After running, refresh this page — the file list will show <span className="font-mono">Fairy_Tales/</span>, <span className="font-mono">Hansel_Gretel/</span>, etc. directly.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sync Result */}
      {syncResult && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-sm px-4 py-3">
          <p className="text-xs text-emerald-400">Synced: {syncResult.items_created} items created from NAS</p>
          <button onClick={() => setSyncResult(null)} className="text-[10px] text-emerald-500/50 mt-1 hover:text-emerald-400">Dismiss</button>
        </div>
      )}

      {/* Config Panel */}
      {showConfig && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">NAS Configuration</h3>
          <p className="text-[10px] text-zinc-500 mb-3">Add these to <span className="font-mono text-zinc-400">/app/backend/.env</span>:</p>
          <pre className="text-[10px] font-mono text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-sm p-3 leading-relaxed">{`NAS_HOST=172.16.16.3
NAS_SHARE=Projects
NAS_BASE_PATH=
NAS_USERNAME=your_nas_username
NAS_PASSWORD=your_nas_password
NAS_PORT=445`}</pre>
          <p className="text-[10px] text-zinc-600 mt-2">VawworldOS connects to QNAP via SMB (port 445). Requires VPN if cloud-deployed.</p>
        </div>
      )}

      {/* Map Project Modal */}
      {showMap && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="map-nas-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-96">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Map NAS Folder to Project</h3>
            <p className="text-xs text-zinc-500 mb-3">Select which NAS folder corresponds to "{projectName}"</p>
            <div className="mb-3">
              <label className="text-[10px] text-zinc-500 uppercase">NAS Project Folder</label>
              <select value={mapForm.nas_folder} onChange={e => setMapForm({ nas_folder: e.target.value })}
                className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                <option value="">Select folder...</option>
                {(status?.projects_on_nas || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={mapProject} disabled={!mapForm.nas_folder} className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm disabled:opacity-40">Map Project</button>
              <button onClick={() => setShowMap(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* File Browser - show when bridge is connected OR NAS is directly connected */}
      {(isConnected || isMapped || hasBridge) && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          {/* Breadcrumb */}
          <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2">
            {currentPath && (
              <button onClick={navigateUp} className="h-6 w-6 bg-zinc-800 hover:bg-zinc-700 rounded-sm flex items-center justify-center">
                <ArrowLeft className="w-3 h-3 text-zinc-400" />
              </button>
            )}
            <button onClick={() => browse('')} className="text-[10px] text-zinc-500 hover:text-blue-400 transition-colors">
              {isMapped ? mapping.nas_folder : (hasBridge ? (bridgeAgents[0]?.nas_base_path || `${bridgeAgents[0]?.nas_share || 'Projects'} (root)`) : (status?.base_path || 'Root'))}
            </button>
            {currentPath.split('/').filter(Boolean).map((part, i, arr) => (
              <React.Fragment key={i}>
                <ChevronRight className="w-3 h-3 text-zinc-700" />
                <button onClick={() => browse(arr.slice(0, i + 1).join('/'))} className="text-[10px] text-zinc-400 hover:text-blue-400">{part}</button>
              </React.Fragment>
            ))}
            <button onClick={() => browse(currentPath)} className="ml-auto h-6 px-2 bg-zinc-800 text-zinc-500 text-[10px] rounded-sm hover:bg-zinc-700">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>

          {/* File List */}
          {loading ? (
            <div className="px-4 py-8 text-center text-xs text-zinc-600">Loading files...</div>
          ) : files.length === 0 && !currentPath ? (
            <div className="px-4 py-8 text-center">
              <HardDrive className="w-8 h-8 text-zinc-800 mx-auto mb-2" />
              <p className="text-xs text-zinc-600">Click "Browse" or navigate to see files</p>
              <button onClick={() => browse('')} className="mt-2 h-7 px-4 bg-blue-600 text-white text-[10px] rounded-sm">Browse NAS</button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/30 max-h-[50vh] overflow-y-auto">
              {files.map((f, i) => {
                const ext = f.extension || '';
                const FileIcon = f.is_dir ? Folder : (EXT_ICONS[ext] || File);
                const color = f.is_dir ? '#f59e0b' : (EXT_COLORS[ext] || '#71717a');
                return (
                  <div key={i} className="px-4 py-2 flex items-center gap-3 hover:bg-zinc-800/20 transition-colors group" data-testid={`nas-file-${f.name}`}>
                    <FileIcon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                    {f.is_dir ? (
                      <button onClick={() => browse(f.path)} className="flex-1 text-left text-xs text-zinc-200 hover:text-blue-400 transition-colors font-medium">{f.name}</button>
                    ) : (
                      <span className="flex-1 text-xs text-zinc-400">{f.name}</span>
                    )}
                    {!f.is_dir && <span className="text-[9px] font-mono text-zinc-600">{f.size_human || ''}</span>}
                    <span className="text-[9px] font-mono text-zinc-700">{f.modified ? new Date(f.modified).toLocaleDateString() : ''}</span>
                    {!f.is_dir && (
                      <button onClick={() => linkToLibrary(f.path)} className="h-5 px-2 bg-blue-500/10 text-blue-400 text-[9px] rounded-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        <Link2 className="w-2.5 h-2.5" /> Link
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Not configured / Bridge Setup */}
      {!isConnected && (
        <div className="space-y-4">
          {/* Bridge Agent Status */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center gap-3 mb-4">
              <Terminal className="w-5 h-5 text-cyan-400" />
              <div>
                <h3 className="text-sm font-heading font-bold text-white">NAS Bridge Agent</h3>
                <p className="text-[10px] text-zinc-500">
                  {bridgeAgents.length > 0
                    ? `Connected! Agent: ${bridgeAgents[0]?.agent_id || 'active'} | NAS: ${bridgeAgents[0]?.nas_host || '172.16.16.3'} | Last seen: ${bridgeAgents[0]?.last_seen ? new Date(bridgeAgents[0].last_seen).toLocaleString() : 'just now'}`
                    : 'No bridge agents connected yet - follow steps below'}
                </p>
              </div>
              {bridgeAgents.length > 0 && <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />}
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 mb-4">
              <h4 className="text-xs font-semibold text-white mb-1">Persistent WebSocket Bridge v5.0 — Always On</h4>
              <p className="text-[10px] text-zinc-500 mb-3">New daemon holds a single always-open connection with auto-reconnect, heartbeat, QNAP autorun + cron watchdog. Survives reboots, SSH disconnects, firmware updates.</p>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">1</span>
                  <div className="flex-1">
                    <p className="text-xs text-zinc-200">SSH into your QNAP</p>
                    <code className="block text-[10px] font-mono text-cyan-400 bg-zinc-900 px-2 py-1 rounded mt-1">ssh admin@172.16.16.3</code>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">2</span>
                  <div className="flex-1">
                    <p className="text-xs text-zinc-200">Create install dir & drop in daemon + installer</p>
                    <code className="block text-[10px] font-mono text-cyan-400 bg-zinc-900 px-2 py-1 rounded mt-1">mkdir -p /share/CACHEDEV1_DATA/Projects/vos_bridge{`
`}cd /share/CACHEDEV1_DATA/Projects/vos_bridge</code>
                    <button data-testid="get-bridge-script" onClick={downloadScript}
                      className="mt-2 h-7 px-4 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
                      <Download className="w-3 h-3" /> Get Daemon + Installer
                    </button>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">3</span>
                  <div className="flex-1">
                    <p className="text-xs text-zinc-200">One-shot install (everything: autostart, watchdog, daemon)</p>
                    <code className="block text-[10px] font-mono text-cyan-400 bg-zinc-900 px-2 py-1 rounded mt-1">chmod +x INSTALL_NAS_DAEMON.sh && sh INSTALL_NAS_DAEMON.sh</code>
                    <p className="text-[10px] text-zinc-500 mt-1">Installs autorun.sh (reboot-proof) + cron watchdog (crash-proof). Never manually restart again.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bridge Script Modal */}
          {showBridgeSetup && bridgeScript && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="bridge-script-modal">
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[60rem] max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-heading font-bold text-white">VawworldOS Bridge Daemon v5.0 + Installer</h3>
                    <p className="text-[10px] text-zinc-400">Copy BOTH files to your QNAP at <span className="font-mono text-cyan-400">/share/CACHEDEV1_DATA/Projects/vos_bridge/</span></p>
                  </div>
                  <button onClick={() => setShowBridgeSetup(false)} className="h-7 px-3 bg-zinc-800 text-zinc-400 text-[10px] rounded-sm">Close</button>
                </div>
                <div className="flex-1 overflow-y-auto space-y-4">
                  {/* Installer block */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[11px] font-semibold text-cyan-300">1. INSTALL_NAS_DAEMON.sh <span className="text-zinc-500 font-normal">(run this first)</span></h4>
                      <button data-testid="copy-installer-btn" onClick={copyInstaller}
                        className={`h-6 px-3 text-[10px] font-medium rounded-sm flex items-center gap-1.5 ${copied === 'installer' ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                        <Copy className="w-3 h-3" /> {copied === 'installer' ? 'Copied!' : 'Copy Installer'}
                      </button>
                    </div>
                    <pre className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-[9px] font-mono text-zinc-300 whitespace-pre leading-relaxed max-h-64 overflow-y-auto">{bridgeScript.installer_content || '# (installer missing — backend needs redeploy)'}</pre>
                  </div>
                  {/* Daemon block */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-[11px] font-semibold text-cyan-300">2. vos_bridge_daemon.py <span className="text-zinc-500 font-normal">(the brain)</span></h4>
                      <button data-testid="copy-script-btn" onClick={copyScript}
                        className={`h-6 px-3 text-[10px] font-medium rounded-sm flex items-center gap-1.5 ${copied === true ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                        <Copy className="w-3 h-3" /> {copied === true ? 'Copied!' : 'Copy Daemon'}
                      </button>
                    </div>
                    <pre className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-[9px] font-mono text-zinc-300 whitespace-pre leading-relaxed max-h-80 overflow-y-auto">{bridgeScript.content}</pre>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-800 flex gap-2 flex-wrap">
                  {(bridgeScript.instructions || []).map((inst, i) => (
                    <span key={i} className="text-[9px] px-2 py-1 bg-zinc-800 text-zinc-300 rounded">{inst}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {showConfig && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Direct NAS Configuration (Alternative)</h3>
              <p className="text-[10px] text-zinc-500 mb-3">Only needed if VawworldOS backend can directly reach your NAS (same network or VPN):</p>
              <pre className="text-[10px] font-mono text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-sm p-3">{`NAS_HOST=172.16.16.3
NAS_SHARE=Projects
NAS_BASE_PATH=
NAS_USERNAME=Admin
NAS_PASSWORD=your_password
NAS_PORT=445`}</pre>
            </div>
          )}
        </div>
      )}

      {/* Create Folder Structure Modal */}
      {showCreateFolders && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="create-folders-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[44rem] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-heading font-semibold text-white">Create NAS Folder Structure</h3>
                <p className="text-[10px] text-zinc-400 mt-0.5">Browse your NAS, pick a location, and drop the full Blender Studio structure there.</p>
              </div>
              <button onClick={() => { setShowCreateFolders(false); setJobId(null); setJobStatus(null); setFolderResult(null); }}
                className="text-zinc-500 hover:text-white text-xs">Close</button>
            </div>

            {/* NAS Picker */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[9px] text-zinc-400 uppercase tracking-wider">1. Pick target location on NAS</label>
                <button data-testid="picker-browse-btn" onClick={() => browsePicker('')}
                  className="h-6 px-2 bg-blue-600 hover:bg-blue-500 text-white text-[10px] rounded-sm flex items-center gap-1">
                  <Folder className="w-3 h-3" /> Browse NAS
                </button>
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="px-3 py-1.5 border-b border-zinc-800 flex items-center gap-1.5 text-[10px]">
                  {pickerPath && (
                    <button onClick={pickerUp} className="h-5 px-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-sm">
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                  )}
                  <button onClick={() => browsePicker('')} className="text-zinc-400 hover:text-blue-400">Root</button>
                  {pickerPath.split('/').filter(Boolean).map((part, i, arr) => (
                    <React.Fragment key={i}>
                      <ChevronRight className="w-3 h-3 text-zinc-700" />
                      <button onClick={() => browsePicker(arr.slice(0, i + 1).join('/'))} className="text-zinc-300 hover:text-blue-400">{part}</button>
                    </React.Fragment>
                  ))}
                  <span className="ml-auto text-zinc-500 font-mono">{folderBase ? `Selected: ${folderBase}` : '(no location picked → root)'}</span>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {pickerLoading ? (
                    <p className="text-[10px] text-zinc-500 text-center py-4">Scanning NAS... (bridge must be running)</p>
                  ) : pickerDirs.length === 0 ? (
                    <p className="text-[10px] text-zinc-600 text-center py-4">No folders here. Click "Browse NAS" above or go up.</p>
                  ) : pickerDirs.map((f, i) => (
                    <div key={i} className="px-3 py-1.5 flex items-center gap-2 hover:bg-zinc-800/40 group">
                      <Folder className="w-3.5 h-3.5 text-amber-400" />
                      <button onClick={() => browsePicker(f.path)}
                        className="flex-1 text-left text-[11px] text-zinc-200 hover:text-blue-400 truncate">{f.name}</button>
                      <button data-testid={`picker-select-${f.name}`} onClick={() => setFolderBase(f.path)}
                        className={`h-5 px-2 text-[9px] rounded-sm transition-colors ${folderBase === f.path ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-blue-600 hover:text-white'}`}>
                        {folderBase === f.path ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-1.5 border-t border-zinc-800 flex items-center justify-between">
                  <span className="text-[9px] text-zinc-500">Or place at current folder:</span>
                  <button data-testid="picker-select-current" onClick={() => setFolderBase(pickerPath)}
                    className={`h-5 px-2 text-[9px] rounded-sm ${folderBase === pickerPath && pickerPath ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-blue-600 hover:text-white'}`}>
                    Use "{pickerPath || 'root'}"
                  </button>
                </div>
              </div>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[9px] text-zinc-400 uppercase tracking-wider">Target Location (NAS Base)</label>
                <input data-testid="folder-base-input" value={folderBase} onChange={e => setFolderBase(e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono focus:outline-none focus:border-blue-500"
                  placeholder="(empty = NAS root)" />
                <p className="text-[9px] text-zinc-500 mt-1">Picked from browser above. Editable.</p>
              </div>
              <div>
                <label className="text-[9px] text-zinc-400 uppercase tracking-wider">2. Project Folder Name *</label>
                <input data-testid="folder-name-input" value={folderName} onChange={e => setFolderName(e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono focus:outline-none focus:border-blue-500"
                  placeholder={projectName?.replace(/\s+/g, '_') || '02_Fairy_Tales'} />
                <p className="text-[9px] text-zinc-500 mt-1">New folder that will be created.</p>
              </div>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 mb-4 text-[9px] font-mono text-zinc-400 max-h-44 overflow-y-auto leading-relaxed">
              <p className="text-zinc-200 font-bold mb-1">Full path that will be built:</p>
              <p className="text-cyan-400 break-all">
                &lt;NAS_ROOT&gt;/{folderBase ? <span className="text-amber-300">{folderBase}/</span> : <span className="text-rose-400 underline">⚠ no parent — will go straight to root</span>}<span className="text-emerald-300">{folderName || 'PROJECT_NAME'}</span>/&lt;Chapter_NN&gt;/
              </p>
              <p className="mt-2 text-zinc-500">
                <span className="text-zinc-300">00_Doc</span> · <span className="text-zinc-300">01_Pre_Production</span> (001_Script + VO/, <span className="text-emerald-400">002_Concept_Art (Character/Environment/Props)</span>, 003_Animatics, 004_Cut_by_Animatics) · <span className="text-zinc-300">02_Library</span> (Char/BG/Props) · <span className="text-zinc-300">03_Production</span> (001_Anim/<span className="text-emerald-400">Layout</span>, 001_Anim/<span className="text-emerald-400">Anim</span>, 002_Lighting, 003_Comp, 004_Render) · 04_Post_Production · 05_Feedback · 06_Delivery · 07_From_Client · 08_To_Client · Render_Fram. Created per chapter.
              </p>
            </div>

            {folderResult && !folderResult.error && (
              <div className="mb-3 px-3 py-2 rounded-sm text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                Queued {folderResult.folders_planned} mkdir commands at <span className="font-mono">{folderResult.root}</span> via bridge <span className="font-mono">{folderResult.bridge_agent}</span>
              </div>
            )}
            {folderResult?.error && (
              <div className="mb-3 px-3 py-2 rounded-sm text-[10px] bg-red-500/10 text-red-400 border border-red-500/20">{folderResult.error}</div>
            )}
            {jobStatus && (
              <div className="mb-3 px-3 py-2 rounded-sm bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-blue-300">Bridge progress</span>
                  <span className="text-zinc-400 font-mono">{jobStatus.completed}/{jobStatus.total} done · {jobStatus.failed} failed</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-sm mt-2 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(jobStatus.completed / Math.max(jobStatus.total, 1)) * 100}%` }} />
                </div>
                {jobStatus.done && <p className="text-[10px] text-emerald-400 mt-1.5">All folders created on NAS</p>}
              </div>
            )}

            <div className="flex gap-2">
              <button data-testid="create-folders-submit-btn" onClick={() => {
                  // Hard guardrail: block accidental NAS-root creates.
                  // Forces the producer to either pick a parent folder or
                  // type one — protects against the silent "create at root"
                  // bug that dropped Hansel&Gretel/ at /Projects/ instead
                  // of inside /Projects/01FAIRY_TALES/.
                  if (!folderBase) {
                    const ok = window.confirm(
                      `⚠️ No parent folder picked.\n\nThis will create "${folderName}/" directly at the NAS share root (/Projects/${folderName}/), NOT inside any sub-folder.\n\nIf you wanted it inside (e.g.) 01FAIRY_TALES/, click Cancel and either:\n  • Click "Browse NAS" → click on 01FAIRY_TALES (auto-selects it), or\n  • Type the parent path in "Target Location" below.\n\nProceed with creating at NAS root?`
                    );
                    if (!ok) return;
                  }
                  submitCreateFolders();
                }}
                disabled={!folderName}
                className="flex-1 h-9 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold rounded-sm disabled:opacity-40 flex items-center justify-center gap-2">
                <Database className="w-3.5 h-3.5" /> Create Folder Structure on NAS
              </button>
              <button onClick={() => setShowCreateFolders(false)} className="w-24 h-9 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
